import {combineReducers} from 'redux'
import Immutable from 'seamless-immutable'
import types from '../../../actions/types'
import {createPivotTableReducer} from 'reducers/pivotTable'
import {isNil, set} from 'lodash'
import QuoteHelper from '../../../helpers/quotes'
import {invoiceChangeActions} from 'helpers/invoices'
import itemDrawer from './itemDrawer'
import {
  asyncMarkInvoiceAsPosted,
  unmarkInvoiceAsPosted,
  updateAccountingInvoiceId,
} from 'helpers/accounting'
import {createNewHistoryItem} from 'containers/quoteEditor/components/quoteHistoryView/columnDefinition'
import {DataType} from 'helpers/socket'

export const loading = (state = Immutable(false), action) => {
  switch (action.type) {
    case types.INVOICING_LOAD_INVOICE_ATTEMPT:
      return Immutable(true)
    case types.INVOICING_LOAD_INVOICE_FAILURE:
    case types.INVOICING_LOAD_INVOICE_SUCCESS:
      return Immutable(false)
    default:
      return state
  }
}

export const invoice = (state = null, action) => {
  const currentState = isNil(state)
      ? null
      : Immutable.asMutable(state, {deep: true})

  switch (action.type) {
    case types.INVOICING_LOAD_INVOICE_SUCCESS:
      return Immutable(action.data)

    case types.INVOICING_LOAD_INVOICE_ATTEMPT:
    case types.INVOICING_LOAD_INVOICE_FAILURE:
    case types.INVOICING_UNLOAD_INVOICE:
      return null

    case types.INVOICING_INVOICE_UPDATE_FROM_JOB_SUCCESS:
      return Immutable(action.data.result)

    case types.INVOICING_INVOICE_UPDATE_INVOICE:
      return Immutable(action.invoice)

    case types.INVOICING_INVOICE_UPDATE_PROPERTY:
      if (isNil(currentState)) return null
      const newState = set(currentState, action.key, action.value)
      return Immutable(newState)

    case types.INVOICING_MARK_POSTED_SUCCESS:
    case types.ACCOUNTING_INVOICE_POST_ASYNC_SUCCESS:
      return Immutable(asyncMarkInvoiceAsPosted(currentState, action))

    case types.INVOICING_UNMARK_POSTED_SUCCESS:
      return Immutable(unmarkInvoiceAsPosted(currentState, action))

    case types.ACCOUNTING_INVOICE_SHOW_POST_REPORT:
      return Immutable(updateAccountingInvoiceId(currentState, action))

    case types.INVOICING_INVOICE_SAVE_SUCCESS:
      return Immutable({
        ...currentState,
        ...action.data.result,
      })

    default:
      return state
  }
}

export const deletedItems = (state = Immutable([]), action) => {
  switch (action.type) {
    case types.INVOICING_LOAD_INVOICE_SUCCESS:
    case types.INVOICING_LOAD_INVOICE_ATTEMPT:              // reset when loading another invoice
    case types.INVOICING_INVOICE_SAVE_SUCCESS:
      return Immutable([])    // reset if or when saved changes

    case types.INVOICING_INVOICE_DELETE_INVOICE_ITEM:
      return Immutable([
        ...state,
        action.invoiceItem.id,
      ])
    case types.INVOICING_INVOICE_DELETE_INVOICE_ITEMS:
      return Immutable([
        ...state,
        ...action.invoiceItems.map(i => i.id),
      ])
    default:
      return state
  }
}

export const changesMade = (state = Immutable(false), action) => {
  if (invoiceChangeActions.indexOf(action.type) !== -1) {
    return true
  } else {
    switch (action.type) {
      case types.INVOICING_LOAD_INVOICE_SUCCESS:
        return false
      case types.PIVOT_TABLE_EDIT_DATA:
      case types.PIVOT_TABLE_EDIT_DATA_MULTI:
      case types.PIVOT_TABLE_ADD_ENTITY:
        if (action.key === invoiceItemsPivotTableKey) return true
        return state
      default:
        return state
    }
  }
}

export const changeLogInvoice = (state = Immutable([]), action) => {
  switch (action.type) {
    case types.INVOICING_LOAD_INVOICE_SUCCESS:
      return Immutable([action.data])
    case types.INVOICING_LOG_CHANGE:
      return Immutable([...state, action.invoice])
    case types.INVOICING_UNDO_CHANGE:
      return Immutable(state.slice(0, -1))
    default:
      return state
  }
}

export const changeLogInvoiceItems = (state = Immutable([]), action) => {
  switch (action.type) {
    case types.PIVOT_TABLE_LOAD_SUCCESS:
      if (action.key === invoiceItemsPivotTableKey) {
        return Immutable([action.data])
      }
      return state
    case types.INVOICING_LOAD_INVOICE_SUCCESS:
      return Immutable([action.data])
    case types.INVOICING_LOG_CHANGE:
      return Immutable([...state, action.invoiceItems])
    case types.INVOICING_UNDO_CHANGE:
      return Immutable(state.slice(0, -1))
    default:
      return state
  }
}

const observingUsers = (state = {}, action) => {
  switch (action.type) {
    case types.USERS_OBSERVING_INVOICE:
      return action.users

    case types.USERS_OBSERVING:
      return action.dataType === DataType.INVOICE
          ? action.users
          : state

    case types.DEOBSERVE:
      return action.dataType === DataType.INVOICE
          ? {}
          : state

    default:
      return state
  }
}

const createNewItem = (item, state) => ({
  ...item,
  sortOrder: state.length + 1,
  lineItemLetter: QuoteHelper.getOrderItemLetter(state.length + 1), // the same for invoicing
})

export const invoiceItemsPivotTableKey = 'invoicingContext.selected.items'
export const invoiceHistoryPivotTableKey = 'invoicingContext.selected.history'

export default combineReducers({
  loading,
  invoice,
  changesMade,
  changeLogInvoice,
  changeLogInvoiceItems,
  deletedItems,
  itemDrawer,
  items: createPivotTableReducer({
    key: invoiceItemsPivotTableKey,
    defaultSortColumn: Immutable('sortOrder'),
    defaultSortDirection: Immutable('asc'),
    pageSize: 50,
    defaultGridStretch: false,
    defaultAreAllRowsSelected: false,
    defaultCanSelectRows: false,
    retainEdits: false,
    onNewEntity: createNewItem,
  }),
  history: createPivotTableReducer({
    key: invoiceHistoryPivotTableKey,
    defaultSortColumn: 'timestamp',
    defaultSortDirection: 'desc',
    pageSize: 50,
    defaultGridStretch: false,
    defaultAreAllRowsSelected: false,
    defaultCanSelectRows: false,
    retainEdits: false,
    onNewEntity: createNewHistoryItem,
  }),
  observingUsers,
})
